/**
 * One shared record of "this screen is holding work that would be lost".
 *
 * Why a registry rather than a guard per page: React Router allows exactly one
 * active `useBlocker` at a time. A single blocker lives in
 * `NavigationGuardPrompt`; every page that holds work simply registers here.
 * That is what keeps one intended leave to exactly one stay/leave choice, with
 * no duplicate confirms and no history sentinels.
 */

export interface NavigationGuardEntry {
  /** Shown to the person. Written in ordinary installer/office language. */
  message: string;
  /** Called when they choose to stay, so the page can refocus its own work. */
  onBlocked?: () => void;
}

const guards = new Map<string, NavigationGuardEntry>();
const listeners = new Set<() => void>();
let bypassDepth = 0;

function emit(): void {
  listeners.forEach((l) => l());
}

export function subscribeNavigationGuards(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function registerNavigationGuard(id: string, entry: NavigationGuardEntry): void {
  const existing = guards.get(id);
  if (existing && existing.message === entry.message && existing.onBlocked === entry.onBlocked) {
    return;
  }
  guards.set(id, entry);
  emit();
}

export function clearNavigationGuard(id: string): void {
  if (guards.delete(id)) emit();
}

/**
 * The message to show, or null when nothing is held. The earliest-registered
 * guard wins so the wording matches the work that has been outstanding longest.
 */
export function getNavigationGuardMessage(): string | null {
  if (bypassDepth > 0) return null;
  const first = guards.values().next();
  return first.done ? null : first.value.message;
}

export function hasNavigationGuard(): boolean {
  return getNavigationGuardMessage() !== null;
}

/** Tell every holding page that the person chose to stay. */
export function notifyNavigationBlocked(): void {
  guards.forEach((g) => g.onBlocked?.());
}

/**
 * Run a navigation that must never be questioned — signing out, a session that
 * has been revoked, or a redirect the person did not ask for. The guards stay
 * registered (their pages unmount normally) but nothing is asked.
 */
export function leaveWithoutGuard<T>(run: () => T): T {
  bypassDepth += 1;
  emit();
  try {
    return run();
  } finally {
    // Released on the next tick so the router has already committed the change.
    setTimeout(() => {
      bypassDepth = Math.max(0, bypassDepth - 1);
      emit();
    }, 0);
  }
}

/** Test-only reset. */
export function __resetNavigationGuards(): void {
  guards.clear();
  bypassDepth = 0;
  emit();
}
